/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import { charsets, PasswordPolicy } from "password-sheriff";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "twin.macro";
import tw from "twin.macro";

export const passwordPolicy = new PasswordPolicy({
  length: { minLength: 8 },
  contains: {
    expressions: [
      charsets.lowerCase,
      charsets.upperCase,
      charsets.numbers,
    ],
  },
});

/** @typedef {{ code: string, format: (string | number)[], message: string, verified: boolean, items?: PasswordRule[] }} PasswordRule */

const PasswordRuleList = styled("ul")(tw`space-y-2`);

/** @type {React.FC<{ rule: PasswordRule }>} */
const PasswordRuleListItem = ({ rule }) => {
  const { t } = useTranslation();
  return (
    <li>
      <div tw="flex space-x-2.5">
        {rule.verified ? (
          <CheckIcon tw="flex-shrink-0 h-4 w-4 text-green-500 mt-0.5" aria-hidden="true" />
        ) : (
          <XIcon tw="flex-shrink-0 h-4 w-4 text-gray-400 mt-0.5" aria-hidden="true" />
        )}
        <span tw="text-sm text-gray-500" css={rule.verified && tw`text-green-700 font-medium`}>
          {t(`auth.${rule.code}`, rule.format)}
        </span>
      </div>
      {rule.items && (
        <PasswordRuleList tw="ml-8 mt-2">
          {rule.items.map((rule) => (
            <PasswordRuleListItem key={rule.code} rule={rule} />
          ))}
        </PasswordRuleList>
      )}
    </li>
  );
};

export const PasswordPolicyChecker = ({ password }) => {
  const { rules, verified } = useMemo(() => passwordPolicy.missing(password), [password]);
  return (
    <section tw="p-4 rounded-md bg-gray-50" css={verified && tw`bg-green-50`}>
      <PasswordRuleList>
        {rules.map((rule) => (
          <PasswordRuleListItem key={rule.code} rule={rule} />
        ))}
      </PasswordRuleList>
    </section>
  );
};
